import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faTrash, 
  faGripLines,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import styles from './EPKBuilder.module.css';

const PerformanceStep = ({ data, updateData, errors, mode }) => {
  // Initialize with a default value if data is missing or empty
  const [performances, setPerformances] = useState(() => {
    if (data.performances && data.performances.length > 0) {
      return data.performances;
    }
    return [{ name: '', year: '', city: '', isHeadliner: false, audienceSize: '' }];
  });
  
  // Use refs to prevent update loops
  const initialRender = useRef(true);
  const prevPerformancesRef = useRef(performances);
  
  // Update parent component when data changes
  useEffect(() => {
    // Skip the update on the initial render
    if (initialRender.current) {
      initialRender.current = false;
      prevPerformancesRef.current = performances;
      return;
    }
    
    // Only update if data has actually changed
    if (JSON.stringify(prevPerformancesRef.current) !== JSON.stringify(performances)) {
      console.log('PerformanceStep: Syncing with parent component', performances);
      updateData(performances);
      
      // Update ref
      prevPerformancesRef.current = performances;
    }
  }, [performances, updateData]);
  
  // Generate year options
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 2000; year--) {
      years.push(year);
    }
    return years;
  };
  
  // Handle input change
  const handleInputChange = (index, field, value) => {
    const updatedPerformances = [...performances];
    
    // Special handling for audience size to ensure it's a number
    if (field === 'audienceSize' && value !== '') {
      // Remove any non-numeric characters
      value = value.replace(/[^0-9]/g, '');
      
      // Convert to number
      value = parseInt(value, 10);
      
      // If not a valid number, set to empty string
      if (isNaN(value)) {
        value = '';
      }
    }
    
    updatedPerformances[index][field] = value;
    setPerformances(updatedPerformances);
  };
  
  // Toggle headliner status
  const toggleHeadliner = (index) => {
    const updatedPerformances = [...performances];
    updatedPerformances[index].isHeadliner = !updatedPerformances[index].isHeadliner;
    setPerformances(updatedPerformances);
  };
  
  // Add new performance
  const addPerformance = () => {
    if (mode === 'view') return;
    
    setPerformances([
      ...performances,
      { name: '', year: '', city: '', isHeadliner: false, audienceSize: '' }
    ]);
  };
  
  // Remove performance
  const removePerformance = (index) => {
    if (mode === 'view') return;
    
    // Don't remove the last performance
    if (performances.length <= 1) return;
    
    const updatedPerformances = [...performances];
    updatedPerformances.splice(index, 1);
    setPerformances(updatedPerformances);
  };
  
  // Check if a performance can be considered complete
  const isPerformanceComplete = (performance) => {
    return performance.name && performance.year;
  };
  
  // Format audience size for display
  const formatAudienceSize = (size) => {
    if (!size) return '';
    return size.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  
  return (
    <div className={`${styles.formSection} ${mode === 'view' ? styles.viewMode : ''}`}>
      <h2 className={styles.sectionTitle}>Performance History</h2>
      <p className={styles.sectionDescription}>
        Add your top 5 live shows, festivals, or important performances. These help venue bookers understand your live experience.
      </p>
      
      {performances.map((performance, index) => (
        <div key={`performance-${index}`} className={styles.performanceItem}>
          <div className={styles.performanceHeader}>
            <div className={styles.performanceTitle}>
              {isPerformanceComplete(performance) 
                ? `${performance.name} (${performance.year})` 
                : `Performance ${index + 1}`}
            </div>
            
            {mode !== 'view' && (
              <button
                className={styles.deletePerformance}
                onClick={() => removePerformance(index)}
                disabled={performances.length <= 1}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
          </div>
          
          <div className={styles.performanceGrid}>
            <div className={styles.formGroup}>
              <label htmlFor={`name-${index}`}>Event Name</label>
              <input
                type="text"
                id={`name-${index}`}
                value={performance.name}
                onChange={(e) => handleInputChange(index, 'name', e.target.value)}
                placeholder="Festival, venue, or event name"
                disabled={mode === 'view'}
              />
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor={`year-${index}`}>Year</label>
              <select
                id={`year-${index}`}
                value={performance.year}
                onChange={(e) => handleInputChange(index, 'year', e.target.value)}
                disabled={mode === 'view'}
              >
                <option value="">Select Year</option>
                {generateYearOptions().map(year => (
                  <option key={year} value={year.toString()}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor={`city-${index}`}>City</label>
              <input
                type="text"
                id={`city-${index}`}
                value={performance.city}
                onChange={(e) => handleInputChange(index, 'city', e.target.value)}
                placeholder="City where event took place"
                disabled={mode === 'view'}
              />
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor={`audienceSize-${index}`}>Audience Size</label>
              <input
                type="text"
                id={`audienceSize-${index}`}
                value={formatAudienceSize(performance.audienceSize)}
                onChange={(e) => handleInputChange(index, 'audienceSize', e.target.value)}
                placeholder="Approximate number of people"
                disabled={mode === 'view'}
              />
            </div>
          </div>
          
          <div className={styles.performanceSwitch}>
            <label className={styles.switch}>
              <input
                type="checkbox"
                checked={performance.isHeadliner}
                onChange={() => toggleHeadliner(index)}
                disabled={mode === 'view'}
              />
              <span className={styles.slider}></span>
            </label>
            <span>Headlining Performance</span>
          </div>
        </div>
      ))}
      
      {mode !== 'view' && (
        <button 
          type="button" 
          className={styles.addItemBtn}
          onClick={addPerformance}
          disabled={performances.length >= 10}
        >
          <FontAwesomeIcon icon={faPlus} />
          Add Another Performance
        </button>
      )}
      
      {mode !== 'view' && performances.length >= 10 && (
        <div className={styles.maxItemsMessage}>
          Maximum of 10 performances reached
        </div>
      )}
      
      {mode !== 'view' && (
        <div className={styles.tip}>
          <FontAwesomeIcon icon={faCalendarAlt} className={styles.tipIcon} />
          <p>
            Tip: Focus on your most prestigious or largest performances. This helps bookers understand your draw and experience level.
          </p>
        </div>
      )}
    </div>
  );
};

export default PerformanceStep;